import type { JSONSchema4 } from 'json-schema';
import type { PageConfig, PageType, Parser } from '@sap/ux-specification-types';
import type { PageData, PageAnnotations } from '../types';
import { AggregationCreationForm, SCHEMA_CREATION_FORM } from '../types';

import { ObjectAggregation } from '../ObjectAggregation';
import type { PageEditAggregationData } from '../ObjectAggregation';
import { FilterFieldAggregation } from './FilterFieldAggregation';

export const VISUAL_FILTER_PROPERTY_NAME = 'visualFilters';

/**
 * Represents an aggregation for filter fields objects.
 */
export class FilterFieldsAggregation extends ObjectAggregation {
    sortableList = true;
    allowedAnnotationCreationForms = [AggregationCreationForm.NativeFilterFields];
    childClass = FilterFieldAggregation;
    i18nKey = 'FILTER_FIELDS';

    /**
     * Creates an instance of `FilterFieldsAggregation`.
     *
     * @param data Optional aggregation data object used to initialize properties.
     * @param schema Optional JSON schema fragment associated with this aggregation.
     */
    constructor(data?: PageEditAggregationData, schema?: JSONSchema4) {
        super(data, schema);
        const additionalProperties = schema?.additionalProperties;
        if (typeof additionalProperties === 'object' && additionalProperties.$ref && !this.isMacrosNode()) {
            this.schemaCreationForms = [
                {
                    name: AggregationCreationForm.CustomFilterField,
                    kind: SCHEMA_CREATION_FORM,
                    title: 'PAGE_EDITOR_OUTLINE_ADD_CUSTOM_FILTER_FIELDS_TITLE',
                    disabled: false
                }
            ];
        }
    }

    /**
     * Overwritten method for data update of object page actions
     * Method receives current values for actions and detects custom actions.
     *
     * @param data Data which should be used for value population.
     * @param page Page config data.
     * @param pageType Page type.
     * @param path Aggregation path.
     * @param annotations Annotations data.
     */
    public updatePropertiesValues(
        data: PageData,
        page: PageConfig,
        pageType: PageType,
        path: Parser.PropertyPath,
        annotations: PageAnnotations
    ): void {
        super.updatePropertiesValues(data, page, pageType, path, annotations);
        this.formSchema = this.additionalProperties?.aggregations['selectionFields'];
        const filterFields = data || {};
        for (const id in filterFields) {
            const filterField = this.aggregations[id] as FilterFieldAggregation;
            if (filterField?.schema && !filterField.schema.annotationPath) {
                filterField.markAsCustomFilterField();
            }
        }
    }
}
